<template>
  <div>
    <SettingsPagePanelHeader>
      <template #title>カレンダー連携設定</template>
    </SettingsPagePanelHeader>
    <SettingsTable>
      <!-- Google OAuth アプリ申請中なので、現時点で利用できる・利用したい 400F にのみ解放する -->
      <SettingsTableRow name="Googleカレンダー連携" v-if="viewer?.is400FOA">
        <div :class="$style.row">
          <FlatButton
            variant="secondary"
            size="medium"
            @click="handleGoogleClick"
            :disabled="
              connectedProvider != null &&
              connectedProvider !== OAuthProvider.Google
            "
          >
            <div :class="$style.googleButton">
              <!--
                   Google のガイドラインに則るために、アイコンを置く
                   https://about.google/brand-resource-center/guidance/apis/#using-our-icons
              -->
              <GoogleIcon :class="$style.googleButton__icon" />
              <span>
                {{ googleButtonText }}
              </span>
            </div>
          </FlatButton>
        </div>
        <p :class="$style.connectDescription">
          ※Google API
          から受信した情報の使用および他のアプリへの転送は、制限付き使用要件を含む<a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Servicesユーザーデータポリシー</a
          >に従うものとします。
        </p>
      </SettingsTableRow>

      <SettingsTableRow name="Outlook 予定表 連携">
        <div :class="$style.row">
          <p
            v-if="connectedProvider === OAuthProvider.Microsoft"
            :class="$style.connected"
          >
            連携済み
          </p>
          <div>
            <FlatButton
              variant="secondary"
              size="medium"
              @click="handleMicrosoftButtonClick"
              :disabled="
                connectedProvider != null &&
                connectedProvider !== OAuthProvider.Microsoft
              "
              >{{ microsoftButtonText }}
            </FlatButton>
            <p
              v-if="connectedProvider !== OAuthProvider.Microsoft"
              :class="$style.connectDescription"
            >
              Microsoft のサイトに移動します
            </p>
          </div>
        </div>
      </SettingsTableRow>

      <SettingsTableRow
        name="オカネコカレンダー設定"
        v-if="isInAdministratorTeam"
      >
        <div :class="$style.row">
          <div :class="$style.bookingRequest">
            <div :class="$style.caution">
              ※他のメンバーの設定も変更されますのでご注意ください
            </div>
            <div :class="$style.bookingRequest__label">
              <span>選択できる時間枠</span>
            </div>
            <ul
              :class="[$style.options, $style['options--bookingRequest']]"
              v-for="(
                optionGroup, i
              ) in bookingRequestAvailableTimeSlotRangeOptionGroups"
              :key="i"
            >
              <li
                :class="$style.options__item"
                v-for="(option, j) in optionGroup"
                :key="j"
              >
                <label :class="$style.option">
                  <DolphinRadioButton
                    name="availableTimeSlotRange"
                    :model-value="formRef.availableTimeSlotRange"
                    @update:model-value="
                      updateBookingRequestAvailableTimeSlotRange
                    "
                    :value="option.value"
                  />
                  <span :class="$style.option__text">
                    {{ option.text }}
                  </span>
                </label>
              </li>
            </ul>
            <div :class="$style.row__submit">
              <FlatButton variant="primary" size="medium" @click="submit"
                >保存する</FlatButton
              >
            </div>
          </div>
        </div>
      </SettingsTableRow>
    </SettingsTable>
  </div>
</template>

<script setup lang="ts">
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import type { Option, OptionValueType } from '@/types/app'
import { computed, ref, watchEffect } from 'vue'

import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsTableRow from '../SettingsTable/SettingsTableRow.vue'
import { useMicrosoftAuthorizationForPlannerAccount } from '@/hooks/useMicrosoftAuthorization'
import {
  ExtraBookingRequestAvailableTimeSlotRange,
  OAuthProvider,
} from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'
import { useGoogleAuthorizationForPlannerAccount } from '@/hooks/useGoogleAuthorization'
import GoogleIcon from './GoogleIcon.vue'
import {
  useFetchOnCalendarAuthViewQuery,
  useUpdatePlannerBookingRequestCalendarSettingMutation,
} from './CalendarAuthView.generated'
import { captureException } from '@sentry/vue'

const viewQuery = useFetchOnCalendarAuthViewQuery()

const alert = useAlert()

const viewer = computed(() => viewQuery.result.value?.viewer)

const connectedProvider = computed(
  () => viewer.value?.staff.connectedThirdPartyProvider
)

const isInAdministratorTeam = computed(
  () => viewer.value?.staff.role !== 'MEMBER'
)

const hasGoogleOAuth = computed(
  () => viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Google
)

const googleOAuthScopes = ['https://www.googleapis.com/auth/calendar.events']

const googleAuthorization =
  useGoogleAuthorizationForPlannerAccount(googleOAuthScopes)
googleAuthorization.onDone(() => {
  viewQuery.refetch()
  alert.success('Googleカレンダーと連携しました。')
})

const handleGoogleClick = async () => {
  try {
    if (hasGoogleOAuth.value) {
      await googleAuthorization.revoke()
      viewQuery.refetch()
      alert.success('Googleカレンダー連携を解除しました。')
    } else {
      googleAuthorization.requestAndSendCode()
    }
  } catch (e) {
    captureException(e)
    console.error(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const googleButtonText = computed(() =>
  viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Google
    ? '解除する'
    : '連携する'
)

const microsoftButtonText = computed(() =>
  viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Microsoft
    ? '解除する'
    : '連携する'
)

const msAuth = useMicrosoftAuthorizationForPlannerAccount()

const hasMicrosoftOAuth = computed(
  () =>
    viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Microsoft
)

const handleMicrosoftButtonClick = async () => {
  try {
    if (hasMicrosoftOAuth.value) {
      await msAuth.revoke()
      viewQuery.refetch()
      alert.success('Outlook 予定表 連携を解除しました。')
    } else {
      const url = await msAuth.issueOAuthUrl()
      window.location.href = url
    }
  } catch (e) {
    captureException(e)
    console.error(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const bookingRequestAvailableTimeSlotRangeOptionGroups = computed<
  Array<Array<Option<ExtraBookingRequestAvailableTimeSlotRange>>>
>(() => {
  return [
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.Anytime,
        text: '制限しない',
      },
    ],
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayAnytime,
        text: '平日 終日',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayDaytime,
        text: '平日 日中',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayNight,
        text: '平日 夕方以降',
      },
    ],
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendAnytime,
        text: '土日 終日',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendDaytime,
        text: '土日 日中',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendNight,
        text: '土日 夕方以降',
      },
    ],
  ]
})

export type SettingTableForm = {
  availableTimeSlotRange: ExtraBookingRequestAvailableTimeSlotRange
}

const formRef = ref<SettingTableForm>({
  availableTimeSlotRange: ExtraBookingRequestAvailableTimeSlotRange.Anytime,
})

watchEffect(() => {
  formRef.value.availableTimeSlotRange =
    viewer.value?.bookingRequestCalendarSetting.availableTimeSlotRange ||
    ExtraBookingRequestAvailableTimeSlotRange.WeekdayDaytime
})

const updateBookingRequestAvailableTimeSlotRange = (v: OptionValueType) => {
  formRef.value.availableTimeSlotRange =
    v as ExtraBookingRequestAvailableTimeSlotRange
}

const settingMutation = useUpdatePlannerBookingRequestCalendarSettingMutation()

const submit = async () => {
  await settingMutation.mutate({
    input: {
      availableTimeSlotRange: formRef.value.availableTimeSlotRange,
    },
  })

  alert.success('設定を更新しました')
}
</script>

<style module lang="scss">
.row {
  display: flex;
  align-items: center;
  gap: 16px;

  &__submit {
    display: flex;
    margin-top: 16px;
  }
}

.connected {
  font-weight: bold;
}

.connectDescription {
  margin-top: 8px;
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;

  a {
    color: $color-text-and-object-low-emphasis;
    text-decoration: underline;
  }
}

.googleButton {
  display: flex;
  gap: 8px;
  align-items: center;

  &__icon {
    width: 20px;
    height: 20px;
  }
}

.bookingRequest {
  display: block;
  width: 100%;

  &__input {
    width: 100%;
    margin-top: 16px;
    margin-bottom: 4px;
  }

  &__label {
    padding: 4px;
    margin-top: 16px;
    margin-bottom: 4px;
    font-weight: 700;
    font-size: 14px;
  }

  &__note {
    font-size: 10px;
    color: $color-text-and-object-low-emphasis;

    &::before {
      content: '*';
      font-size: 10px;
      margin-right: 0.5em;
    }
  }

  &__info {
    margin-top: 16px;
  }
}

.caution {
  font-size: 14px;
  font-weight: bold;
  color: $color-caution-accent;
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &--bookingRequest {
    margin: -16px -40px 0;
  }

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}
</style>
